import { useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import { UnknownKeysParam, ZodObject, ZodRawShape, ZodTypeAny } from 'zod';
import {
    createExperimentalZodFormResolver,
    getDefaults,
    useFormControls,
    UseFormControlsParams,
} from '@mews/framework';

export interface UseIntegrationFormControlsParams<InputValues extends object, OutputValues extends object>
    extends Pick<
        UseFormControlsParams<InputValues, OutputValues>,
        'resolver' | 'defaultValues' | 'onSubmit' | 'values'
    > {
    schema?: ZodObject<ZodRawShape, UnknownKeysParam, ZodTypeAny, OutputValues, InputValues>;
}

export const useIntegrationFormControls = <InputValues extends object, OutputValues extends object = InputValues>({
    defaultValues,
    onSubmit,
    resolver,
    schema,
    values,
}: UseIntegrationFormControlsParams<InputValues, OutputValues>) => {
    const formParams = useMemo(() => {
        const formResolver = resolver ?? (schema ? createExperimentalZodFormResolver(schema) : undefined);
        const formDefaultValues =
            defaultValues ?? (schema ? (getDefaults(schema) as DefaultValues<InputValues>) : undefined);

        return {
            resolver: formResolver,
            defaultValues: formDefaultValues,
            values,
        };
    }, [defaultValues, resolver, schema, values]);

    const { renderFormContent, submit } = useFormControls<InputValues, OutputValues>({
        onSubmit,
        ...formParams,
    });

    return { submit, renderFormContent };
};
